/**
 * Step navigation for intake flow variants.
 *
 * `request-service` skips screening questions, demographics, and email verification — used when an
 * existing portal client requests an additional therapy service. Group Therapy inserts step 14
 * (cohort selection) after therapy type when `group` is selected, and drops topics, provider
 * preferences, and format — the cohort already answers modality, and matching is deferred to the
 * tracker screening call.
 */

export type IntakeFlowVariant = 'full' | 'request-service';

/** Internal step index for Group Therapy cohort selection (does not renumber steps 0–13). */
export const GROUP_SELECTION_STEP = 14;

/** Internal step indices omitted in the request-service variant. */
export const REQUEST_SERVICE_SKIPPED_STEPS = new Set([3, 5, 6]);

/** Which optional steps this run of the flow includes. */
export interface IntakeStepShape {
  hasLocationStep: boolean;
  includeGroupSelection: boolean;
  /**
   * Group-only: skip topics (1), provider preferences (2), and format (4). Format is derived from
   * the cohort modality; matching preferences are collected later on the tracker screening call.
   */
  skipMatchingPreferenceSteps: boolean;
}

export const DEFAULT_STEP_SHAPE: IntakeStepShape = {
  hasLocationStep: false,
  includeGroupSelection: false,
  skipMatchingPreferenceSteps: false,
};

export function isRequestServiceFlow(variant?: IntakeFlowVariant): boolean {
  return variant === 'request-service';
}

/**
 * True when the flow must re-pin `intakeLeadPatientId` to the known portal patient.
 *
 * Request-service always acts on an already-identified client, but two things clear the id:
 * the portal patient id resolves after the reducer's lazy init, and every demographics
 * prefill dispatch (SET_FIRST_NAME, SET_EMAIL, …) nulls it to invalidate public-intake leads.
 * Without re-pinning, submit has no patient and fails.
 */
export function shouldPinIntakeLeadPatientId(
  variant: IntakeFlowVariant | undefined,
  billingPatientId: string | undefined,
  currentLeadPatientId: string | null,
): boolean {
  if (!isRequestServiceFlow(variant) || !billingPatientId) return false;
  return currentLeadPatientId !== billingPatientId;
}

export function needsGroupSelectionStep(therapyTypes: readonly string[]): boolean {
  return therapyTypes.includes('group');
}

/**
 * Pre-booking steps in display order for either variant. Single source of truth for navigation, the
 * step counter, the answered tally, and the total — they must agree or the header shows nonsense.
 */
export function preBookingStepOrder(
  variant: IntakeFlowVariant | undefined,
  shape: IntakeStepShape,
): number[] {
  const steps = [0];
  if (shape.includeGroupSelection) steps.push(GROUP_SELECTION_STEP);
  const linear = isRequestServiceFlow(variant) ? [1, 2, 4, 7, 8] : [1, 2, 3, 4, 5, 6, 7, 8];
  for (const step of linear) {
    if (shape.skipMatchingPreferenceSteps && (step === 1 || step === 2 || step === 4)) continue;
    if (step === 7 && !shape.hasLocationStep) continue;
    steps.push(step);
  }
  return steps;
}

export function getNextLinearIntakeStep(
  current: number,
  variant: IntakeFlowVariant | undefined,
  shape: IntakeStepShape,
): number {
  const order = preBookingStepOrder(variant, shape);
  const position = order.indexOf(current);
  if (position >= 0 && position < order.length - 1) {
    return order[position + 1];
  }
  return current + 1;
}

export function getPreviousLinearIntakeStep(
  current: number,
  variant: IntakeFlowVariant | undefined,
  ctx: IntakeStepShape & { skipEmailVerification: boolean },
): number {
  const order = preBookingStepOrder(variant, ctx);
  const position = order.indexOf(current);
  if (position > 0) {
    const previous = order[position - 1];
    // Full intake: a client who already verified steps back from payment past the code screen.
    if (current === 8 && previous === 6 && ctx.skipEmailVerification) return 5;
    return previous;
  }
  if (position === 0) return current;
  if (isRequestServiceFlow(variant)) {
    let prev = current - 1;
    while (REQUEST_SERVICE_SKIPPED_STEPS.has(prev) && prev > 0) prev -= 1;
    return prev;
  }
  return current - 1;
}

export function getPreBookingStepCount(
  variant: IntakeFlowVariant | undefined,
  shape: IntakeStepShape,
): number {
  return preBookingStepOrder(variant, shape).length;
}

export function countAnsweredPreBookingSteps(
  activeStep: number,
  variant: IntakeFlowVariant | undefined,
  shape: IntakeStepShape,
  isStepAnswered: (step: number) => boolean,
): number {
  const order = preBookingStepOrder(variant, shape);
  const position = order.indexOf(activeStep);
  // Past the pre-booking steps, they are all behind the client and all count.
  const lastCounted = position >= 0 ? position : order.length - 1;
  let n = 0;
  for (let i = 0; i <= lastCounted; i++) {
    if (isStepAnswered(order[i])) n += 1;
  }
  return n;
}

export function getDisplayStepNumber(
  activeStep: number,
  variant: IntakeFlowVariant | undefined,
  ctx: IntakeStepShape & {
    showingLoading: boolean;
    preBookingCount: number;
    currentBookingRound: number;
    stepsPerRound: number;
    firstRoundStep: number;
    effectiveTotal: number;
    bookingRoundCount?: number;
  },
): number {
  if (ctx.showingLoading) return ctx.preBookingCount + 1;
  const position = preBookingStepOrder(variant, ctx).indexOf(activeStep);
  if (position >= 0) {
    return position + 1;
  }
  if (activeStep >= 9 && activeStep <= 12) {
    // With no booking rounds the screening call is the only screen between loading and review.
    if (ctx.bookingRoundCount === 0) {
      return ctx.preBookingCount + 2;
    }
    return (
      ctx.preBookingCount +
      ctx.currentBookingRound * ctx.stepsPerRound +
      (activeStep - ctx.firstRoundStep) +
      1
    );
  }
  return ctx.effectiveTotal;
}
